import { css } from '@emotion/react';
import type { Env0Theme } from 'constants/themes.constants';

interface GetCommonInputCssFromPropsArgs {
  invalid?: boolean;
  theme: Env0Theme;
}

/**
 * Only what the antd Input block in `antd.constants` cannot express: antd has one placeholder colour
 * where the design has three, it reuses `colorBorder` for the disabled border, and `invalid` is our
 * own prop rather than antd's `status`.
 *
 * Doubled class throughout - antd declares each of these on `.ant-input` behind a zero-specificity
 * `:where()`, which a single emotion class only ties.
 *
 * Every rule carries a descendant twin. rc-input hands the className to the input element only for a
 * bare field (`BaseInput` line 73): a prefix or suffix moves it to `.ant-input-affix-wrapper`, where
 * `::placeholder` matches nothing and the disabled marker is `-affix-wrapper-disabled` rather than
 * `ant-input-disabled`. Addons no longer move it: the wrappers render them beside the field
 * (`input-addons`). The descendant form is written last so a `:where()`-free antd rule never wins on
 * source order.
 */
const inputCss = ({ invalid }: GetCommonInputCssFromPropsArgs) => css`
  &&::placeholder,
  && .ant-input::placeholder {
    color: var(--component-field-text-placeholder);
  }

  &&:hover::placeholder,
  &&:hover .ant-input::placeholder {
    color: var(--component-field-text-placeholder-hover);
  }

  /* focus-within, not focus: the wrapper never takes focus itself, the input inside it does. */
  &&:focus::placeholder,
  &&:focus-within .ant-input::placeholder {
    color: var(--component-field-text-placeholder-active);
  }

  &&:disabled,
  &&.ant-input-disabled,
  &&.ant-input-number-disabled,
  &&.ant-input-affix-wrapper-disabled {
    border-color: var(--component-field-border-disabled);
  }

  ${
    invalid &&
    css`
      &&,
      &&:hover,
      &&:focus,
      &&.ant-input-focused,
      &&.ant-input-number-focused {
        border-color: var(--component-field-border-error);
      }

      /* The ring has to move with the border. antd draws it from controlOutline, which the Input block
         pins to the lime focus token, and it only swaps to colorErrorOutline under antd's own error
         status - which this file deliberately does not use (see above). Result was a red field wearing
         a lime ring the moment it took focus. Same geometry antd emits, error colour. */
      &&:focus,
      &&:focus-within,
      &&.ant-input-focused,
      &&.ant-input-number-focused,
      &&.ant-input-affix-wrapper-focused {
        box-shadow: 0 0 0 var(--border-width-sm) var(--component-field-border-error);
      }
    `
  }
`;

export const getCommonInputCssFromProps = (args: GetCommonInputCssFromPropsArgs) => inputCss(args);
